import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, interval, map, of, startWith, switchMap } from 'rxjs';
import { environment } from '@env/environment';

export enum SystemStatus {
  OPERATIONAL = 'operational',
  DEGRADED = 'degraded',
  DOWN = 'down',
  MAINTENANCE = 'maintenance'
}

export enum IncidentSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ComponentStatus {
  id: string;
  name: string;
  status: SystemStatus;
  description?: string;
  lastChecked: Date;
}

export interface Incident {
  id: string;
  title: string;
  description: string;
  severity: IncidentSeverity;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  startTime: Date;
  endTime?: Date;
  affectedComponents: string[];
  durationSeconds?: number | null;
  checks?: string[];
}

export interface DailyUptime {
  date: string;
  uptimePercent: number | null;
  downtimeSeconds: number | null;
  incidents: number;
}

export interface HealthCheck {
  status: SystemStatus;
  totalChecks: number;
  successfulChecks: number;
  failedChecks: number;
  lastChecked: Date;
  components: ComponentStatus[];
}

export interface Alert {
  id: string;
  title: string;
  message: string;
  severity: IncidentSeverity;
  timestamp: Date;
  source: string;
  resolved: boolean;
  relatedIncidentId?: string;
}

export interface StatusMeta {
  endpoint: string | null;
  sourceLabel: string;
  historyAvailable: boolean;
  version: string | null;
  startedAt: Date | null;
  uptimeSeconds: number | null;
  responseTimeMs: number | null;
  historyGeneratedAt?: Date | null;
  trackingSince?: Date | null;
}

export interface StatusData {
  overall: HealthCheck;
  recentIncidents: Incident[];
  activeAlerts: Alert[];
  historicalUptime: {
    last24Hours: number | null;
    last7Days: number | null;
    last30Days: number | null;
    last90Days: number | null;
  };
  dailyUptime: DailyUptime[];
  meta: StatusMeta;
}

/** Shape of status/history.json published by the MemberTrace-Status-History Lambda. */
export interface StatusHistoryDocument {
  schemaVersion: number;
  generatedAt: string;
  trackingSince: string;
  source?: string;
  current?: { status: string; openIncidentId: string | null };
  historicalUptime?: Partial<StatusData['historicalUptime']>;
  dailyUptime?: DailyUptime[];
  recentIncidents?: Array<Omit<Incident, 'startTime' | 'endTime'> & { startTime: string; endTime?: string | null }>;
}

/** Same-origin static file (S3 + CloudFront, 60s edge cache). */
export const STATUS_HISTORY_PATH = '/status/history.json';

interface PublicHealthCheckEntry {
  status: string;
  description?: string;
  durationMs?: number;
}

interface PublicHealthResponse {
  service?: string;
  status: string;
  version?: string;
  timestampUtc?: string;
  startedAtUtc?: string | null;
  uptimeSeconds?: number | null;
  totalDurationMs?: number | null;
  checks?: Record<string, PublicHealthCheckEntry>;
}

@Injectable({
  providedIn: 'root'
})
export class StatusService {
  private readonly apiUrl = environment.apiUrl || '';
  private readonly healthEndpoint = this.apiUrl ? `${this.apiUrl}/public/health` : '';

  constructor(private readonly http: HttpClient) {}

  getStatus(): Observable<StatusData> {
    const live$ = this.healthEndpoint
      ? this.http.get<PublicHealthResponse>(this.healthEndpoint).pipe(
          map(response => this.mapHealthResponse(response)),
          catchError((error: HttpErrorResponse) => of(this.mapErrorResponse(error)))
        )
      : of(this.buildUnavailableStatus('Public health endpoint is not configured for this environment.'));

    return forkJoin([live$, this.getHistory()]).pipe(
      map(([live, history]) => this.applyHistory(live, history))
    );
  }

  /** Incident history + daily uptime. Missing or invalid history never breaks the live view. */
  getHistory(): Observable<StatusHistoryDocument | null> {
    return this.http.get<StatusHistoryDocument>(STATUS_HISTORY_PATH).pipe(
      map(doc => (doc && typeof doc === 'object' && Array.isArray(doc.dailyUptime) ? doc : null)),
      catchError(() => of(null))
    );
  }

  private applyHistory(live: StatusData, history: StatusHistoryDocument | null): StatusData {
    if (!history) {
      return live;
    }

    const uptime = history.historicalUptime ?? {};
    const incidents: Incident[] = (history.recentIncidents ?? []).map(incident => ({
      ...incident,
      startTime: this.parseDate(incident.startTime) ?? new Date(NaN),
      endTime: this.parseDate(incident.endTime ?? null) ?? undefined,
      affectedComponents: incident.affectedComponents ?? [],
      checks: incident.checks ?? []
    })).filter(incident => !Number.isNaN(incident.startTime.getTime()));

    return {
      ...live,
      recentIncidents: incidents,
      historicalUptime: {
        last24Hours: uptime.last24Hours ?? null,
        last7Days: uptime.last7Days ?? null,
        last30Days: uptime.last30Days ?? null,
        last90Days: uptime.last90Days ?? null
      },
      dailyUptime: history.dailyUptime ?? [],
      meta: {
        ...live.meta,
        historyAvailable: true,
        historyGeneratedAt: this.parseDate(history.generatedAt),
        trackingSince: this.parseDate(history.trackingSince)
      }
    };
  }

  getStatusPolling(intervalMs: number = 30000): Observable<StatusData> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getStatus())
    );
  }

  private mapHealthResponse(response: PublicHealthResponse): StatusData {
    const checkedAt = this.parseDate(response.timestampUtc) ?? new Date();
    const apiStatus = this.mapProbeStatus(response.status);
    const checks = response.checks ?? {};

    const components: ComponentStatus[] = [
      {
        id: 'api',
        name: 'Public API',
        status: apiStatus,
        description: this.buildApiDescription(response),
        lastChecked: checkedAt
      },
      ...Object.entries(checks).map(([key, check]) => ({
        id: key,
        name: this.getComponentName(key),
        status: this.mapProbeStatus(check.status),
        description: check.description ?? `${this.getComponentName(key)} responded to the public health probe.`,
        lastChecked: checkedAt
      }))
    ];

    const successfulChecks = components.filter(component => component.status === SystemStatus.OPERATIONAL).length;
    const failedChecks = components.length - successfulChecks;
    const activeAlerts = components
      .filter(component => component.status !== SystemStatus.OPERATIONAL)
      .map((component, index) => this.createAlertFromComponent(component, checkedAt, index));

    return {
      overall: {
        status: apiStatus,
        totalChecks: components.length,
        successfulChecks,
        failedChecks,
        lastChecked: checkedAt,
        components
      },
      activeAlerts,
      recentIncidents: [],
      historicalUptime: {
        last24Hours: null,
        last7Days: null,
        last30Days: null,
        last90Days: null
      },
      dailyUptime: [],
      meta: {
        endpoint: this.healthEndpoint,
        sourceLabel: 'Live public health probe',
        historyAvailable: false,
        version: response.version ?? null,
        startedAt: this.parseDate(response.startedAtUtc),
        uptimeSeconds: response.uptimeSeconds ?? null,
        responseTimeMs: response.totalDurationMs ?? null
      }
    };
  }

  private mapErrorResponse(error: HttpErrorResponse): StatusData {
    const payload = this.tryParseHealthPayload(error.error);
    if (payload) {
      return this.mapHealthResponse(payload);
    }

    const message = error.status === 0
      ? 'The public site could not reach the MemberTrace API health endpoint.'
      : `The public health endpoint returned ${error.status}${error.statusText ? ` ${error.statusText}` : ''}.`;

    return this.buildUnavailableStatus(message);
  }

  private buildUnavailableStatus(message: string): StatusData {
    const checkedAt = new Date();
    const apiComponent: ComponentStatus = {
      id: 'api',
      name: 'Public API',
      status: SystemStatus.DOWN,
      description: message,
      lastChecked: checkedAt
    };

    return {
      overall: {
        status: SystemStatus.DOWN,
        totalChecks: 1,
        successfulChecks: 0,
        failedChecks: 1,
        lastChecked: checkedAt,
        components: [apiComponent]
      },
      activeAlerts: [
        {
          id: 'alert-endpoint-unavailable',
          title: 'Public health probe unavailable',
          message,
          severity: IncidentSeverity.HIGH,
          timestamp: checkedAt,
          source: 'public-health-endpoint',
          resolved: false
        }
      ],
      recentIncidents: [],
      historicalUptime: {
        last24Hours: null,
        last7Days: null,
        last30Days: null,
        last90Days: null
      },
      dailyUptime: [],
      meta: {
        endpoint: this.healthEndpoint || null,
        sourceLabel: 'Public health probe',
        historyAvailable: false,
        version: null,
        startedAt: null,
        uptimeSeconds: null,
        responseTimeMs: null
      }
    };
  }

  private createAlertFromComponent(component: ComponentStatus, timestamp: Date, index: number): Alert {
    return {
      id: `alert-${component.id}-${index}`,
      title: `${component.name} requires attention`,
      message: component.description ?? `${component.name} is reporting ${component.status}.`,
      severity: component.status === SystemStatus.DOWN ? IncidentSeverity.HIGH : IncidentSeverity.MEDIUM,
      timestamp,
      source: 'public-health-endpoint',
      resolved: false
    };
  }

  private buildApiDescription(response: PublicHealthResponse): string {
    const segments: string[] = [];

    if (response.service) {
      segments.push(`Service: ${response.service}`);
    }

    if (response.version) {
      segments.push(`Version: ${response.version}`);
    }

    if (typeof response.totalDurationMs === 'number') {
      segments.push(`Probe duration: ${Math.round(response.totalDurationMs)} ms`);
    }

    if (!segments.length) {
      return 'Public API readiness probe completed.';
    }

    return segments.join(' | ');
  }

  private getComponentName(key: string): string {
    const lookup: Record<string, string> = {
      api: 'Public API',
      database: 'Primary Database'
    };

    if (lookup[key]) {
      return lookup[key];
    }

    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private mapProbeStatus(status: string | undefined): SystemStatus {
    switch ((status ?? '').toLowerCase()) {
      case 'healthy':
        return SystemStatus.OPERATIONAL;
      case 'degraded':
        return SystemStatus.DEGRADED;
      case 'maintenance':
        return SystemStatus.MAINTENANCE;
      default:
        return SystemStatus.DOWN;
    }
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private tryParseHealthPayload(value: unknown): PublicHealthResponse | null {
    if (!value || typeof value !== 'object' || !('status' in value)) {
      return null;
    }

    return value as PublicHealthResponse;
  }
}
